import React from 'react';
import { CheckCircle, Shield, Star, Award, Crown, Zap } from 'lucide-react';

const badgeConfig = {
  top_artist: {
    icon: Award,
    label: 'Top Artist',
    color: 'text-amber-300',
    bgColor: 'bg-gradient-to-r from-amber-500/30 to-yellow-500/30',
    borderColor: 'border-amber-400/50',
    description: 'Highly reviewed — 4.5+ stars'
  },
  verified: {
    icon: CheckCircle,
    label: 'Verified',
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/20',
    borderColor: 'border-blue-500/30',
    description: 'Identity verified'
  },
  premium: {
    icon: Crown,
    label: 'Premium',
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-500/20',
    borderColor: 'border-yellow-500/30',
    description: 'Premium member'
  },
  top_rated: {
    icon: Star,
    label: 'Top Rated',
    color: 'text-violet-400',
    bgColor: 'bg-violet-500/20',
    borderColor: 'border-violet-500/30',
    description: '4.8+ rating'
  },
  pro: {
    icon: Zap,
    label: 'Pro',
    color: 'text-green-400',
    bgColor: 'bg-green-500/20',
    borderColor: 'border-green-500/30',
    description: 'Professional artist'
  },
  trusted: {
    icon: Shield,
    label: 'Trusted',
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-500/20',
    borderColor: 'border-cyan-500/30',
    description: '50+ bookings'
  },
  rising_star: {
    icon: Award,
    label: 'Rising Star',
    color: 'text-pink-400',
    bgColor: 'bg-pink-500/20',
    borderColor: 'border-pink-500/30',
    description: 'New & trending'
  }
};

export function VerificationBadge({ type = 'verified', size = 'sm', showLabel = false, showTooltip = true }) {
  const config = badgeConfig[type] || badgeConfig.verified;
  const Icon = config.icon;
  
  const sizeClasses = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  return (
    <div className="relative group inline-flex items-center gap-1" data-testid={`badge-${type}`}>
      <div className={`${config.bgColor} ${config.borderColor} border rounded-full p-1`}>
        <Icon className={`${sizeClasses[size]} ${config.color}`} />
      </div>
      
      {showLabel && (
        <span className={`text-xs font-medium ${config.color}`}>{config.label}</span>
      )}
      
      {showTooltip && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
          {config.description}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
        </div>
      )}
    </div>
  );
}

export function BadgeCollection({ badges = [], size = 'sm' }) {
  if (!badges || badges.length === 0) return null;
  
  return (
    <div className="flex items-center gap-1" data-testid="badge-collection">
      {badges.map((badge, index) => (
        <VerificationBadge key={index} type={badge} size={size} />
      ))}
    </div>
  );
}

export const isTopArtist = (artist) =>
  (artist?.rating ?? 0) >= 4.5 && (artist?.review_count ?? 0) >= 3;

export function ArtistBadges({ artist, size = 'sm' }) {
  const badges = [];
  
  // Determine badges based on artist data
  if (isTopArtist(artist)) {
    badges.push('top_artist');
  }
  if (artist?.is_verified || artist?.verification_status === 'verified') {
    badges.push('verified');
  }
  if (artist?.subscription_type === 'premium') {
    badges.push('premium');
  }
  if (artist?.rating >= 4.8 && artist?.review_count >= 10) {
    badges.push('top_rated');
  }
  if (artist?.total_bookings >= 50) {
    badges.push('trusted');
  }
  if (artist?.is_pro || artist?.subscription_type === 'pro') {
    badges.push('pro');
  }
  if (artist?.is_rising_star || (artist?.created_at && isRecent(artist.created_at) && artist?.rating >= 4.5)) {
    badges.push('rising_star');
  }
  
  // Also include any badges explicitly set on the artist
  if (artist?.badges) {
    artist.badges.forEach(b => {
      if (!badges.includes(b)) badges.push(b);
    });
  }
  
  return <BadgeCollection badges={badges} size={size} />;
}

function isRecent(dateStr) {
  const date = new Date(dateStr);
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  return date > thirtyDaysAgo;
}

export default VerificationBadge;
